import { useEffect, useMemo, useState } from "react";
import { GitCompare, X, Loader2, Trophy, MapPin, Zap, Building, TrendingUp } from "lucide-react";
import api from "../../lib/api";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const fmtRD = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `RD$${(v / 1_000_000).toFixed(1)}MM`;
  if (v >= 1_000)     return `RD$${(v / 1_000).toFixed(0)}K`;
  return `RD$${v.toFixed(0)}`;
};

// Distinctive but on-brand colours for each of the up-to-3 columns.
// Ordered so the first campaign gets the flagship yellow.
const COL_COLORS = ["#f4c400", "#0ea5e9", "#8b5cf6"];

/**
 * Side-by-side campaign benchmark modal for the marketing team.
 *
 * Two phases:
 *   1. Picker — user selects 2-3 campaigns from a searchable checkbox
 *      list. All campaigns come from the same `options.campaigns` list
 *      the FilterBar uses, so the vocabulary is guaranteed to match.
 *   2. Compare — POSTs to `/scorecard/campaign-compare` and renders a
 *      3-column table with leads, conversions, revenue, avg ticket,
 *      top branches, brands and channels. Winners per metric are
 *      highlighted with a small trophy badge.
 *
 * Period + custom date range are inherited from the parent Scorecard so
 * the user compares within the same time window they were browsing.
 */
export default function CampaignCompareModal({
  open, onClose, allCampaigns, period, dateRange, year = 2026,
}) {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [phase, setPhase] = useState("pick");   // "pick" | "compare"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset internal state when the modal reopens so a stale selection
  // from a previous session doesn't reappear.
  useEffect(() => {
    if (open) {
      setSelected([]); setSearch(""); setPhase("pick");
      setData(null); setError(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!allCampaigns) return [];
    if (!search) return allCampaigns;
    const q = search.toLowerCase();
    return allCampaigns.filter((c) => c.toLowerCase().includes(q));
  }, [allCampaigns, search]);

  const toggle = (c) => {
    setSelected((prev) => {
      if (prev.includes(c)) return prev.filter((x) => x !== c);
      if (prev.length >= 3) return prev;   // hard-cap at 3
      return [...prev, c];
    });
  };

  const runCompare = async () => {
    setLoading(true); setError(null);
    try {
      const params = { year, period };
      if (period === "custom") {
        params.start_date = dateRange?.start; params.end_date = dateRange?.end;
      }
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => v != null && search.append(k, v));
      selected.forEach((c) => search.append("campaigns", c));
      const r = await api.get(`/scorecard/campaign-compare?${search.toString()}`);
      setData(r.data);
      setPhase("compare");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 23, 42, 0.75)" }}
      onClick={onClose}
      data-testid="campaign-compare-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col"
        style={{ maxWidth: 1240, maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between"
          style={{ background: "#0f172a", color: "#fff" }}>
          <div className="flex items-center gap-3">
            <div className="rounded-lg flex items-center justify-center"
              style={{ width: 40, height: 40, background: "#f4c400", color: "#0f172a" }}>
              <GitCompare size={20} strokeWidth={2.4}/>
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-tight">
                Comparador de Campañas
              </div>
              <div className="text-xs font-semibold opacity-75 mt-0.5">
                {phase === "pick"
                  ? "Selecciona 2 o 3 campañas para comparar side-by-side"
                  : `${data?.period_label || "—"} · ${selected.length} campañas`}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 transition-colors hover:bg-white/10"
            data-testid="campaign-compare-close">
            <X size={20}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          {phase === "pick" && (
            <PickerBody
              selected={selected} toggle={toggle}
              filtered={filtered} search={search} setSearch={setSearch}
              total={allCampaigns?.length || 0}
            />
          )}
          {phase === "compare" && data && (
            <CompareGrid data={data}/>
          )}
          {error && (
            <div className="mt-4 p-3 rounded-lg text-sm font-semibold"
              style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex items-center justify-between gap-3"
          style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
          <div className="text-xs text-gray-500 font-semibold">
            {phase === "pick"
              ? `${selected.length}/3 seleccionadas`
              : "Los ganadores por métrica se marcan con 🏆"}
          </div>
          <div className="flex items-center gap-2">
            {phase === "compare" && (
              <button
                onClick={() => setPhase("pick")}
                className="px-4 py-2 rounded-lg text-sm font-extrabold text-gray-700 hover:bg-gray-100"
                data-testid="campaign-compare-back">
                Cambiar selección
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-extrabold text-gray-700 hover:bg-gray-100"
              data-testid="campaign-compare-cancel">
              Cerrar
            </button>
            {phase === "pick" && (
              <button
                onClick={runCompare}
                disabled={selected.length < 2 || loading}
                className="px-5 py-2 rounded-lg text-sm font-extrabold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                style={{
                  background: selected.length >= 2 && !loading ? "#0f172a" : "#e5e7eb",
                  color: selected.length >= 2 && !loading ? "#f4c400" : "#9ca3af",
                }}
                data-testid="campaign-compare-run">
                {loading
                  ? <><Loader2 size={14} className="animate-spin"/> Calculando…</>
                  : <>Comparar {selected.length} campañas</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Picker ──────────────────────────────────────────────────────────────

function PickerBody({ selected, toggle, filtered, search, setSearch, total }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Buscar en ${total} campañas…`}
          data-testid="campaign-compare-search"
          className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold min-w-[240px]"
          style={{ border: "1px solid #cbd5e1", background: "#fff" }}
          autoFocus
        />
        <div className="text-xs font-bold text-gray-500">
          {filtered.length} resultado{filtered.length !== 1 && "s"}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="mb-3 p-2 rounded-lg flex items-center gap-2 flex-wrap"
          style={{ background: "#fef3c7", border: "1px solid #fbbf24" }}>
          <div className="text-[10px] font-extrabold text-amber-900 uppercase tracking-wider">
            Seleccionadas:
          </div>
          {selected.map((c, i) => (
            <span key={c}
              className="inline-flex items-center gap-1 text-xs font-extrabold px-2 py-1 rounded"
              style={{ background: COL_COLORS[i], color: "#0f172a" }}>
              {c}
              <button onClick={() => toggle(c)} className="ml-1 hover:opacity-70">
                <X size={11}/>
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-slate-200">
        {filtered.map((c) => {
          const idx = selected.indexOf(c);
          const isSel = idx >= 0;
          const disabled = !isSel && selected.length >= 3;
          return (
            <label key={c}
              className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50"
              style={{
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.4 : 1,
                background: isSel ? "#fef3c7" : undefined,
              }}
              data-testid={`campaign-compare-opt-${c}`}>
              <input
                type="checkbox"
                checked={isSel}
                disabled={disabled}
                onChange={() => toggle(c)}
              />
              <span className="text-sm font-semibold flex-1"
                style={{ fontFamily: "monospace", letterSpacing: "-0.01em" }}>
                {c}
              </span>
              {isSel && (
                <span className="text-[10px] font-extrabold uppercase tracking-wider"
                  style={{ color: "#0f172a", background: COL_COLORS[idx],
                    padding: "2px 6px", borderRadius: 4 }}>
                  Slot {idx + 1}
                </span>
              )}
            </label>
          );
        })}
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-400 font-semibold">
            Ningún resultado para &ldquo;{search}&rdquo;
          </div>
        )}
      </div>
    </>
  );
}

// ─── Compare Grid ────────────────────────────────────────────────────────

function CompareGrid({ data }) {
  const cols = data.campaigns || [];
  if (!cols.length) return null;

  return (
    <div className="grid gap-4" style={{
      gridTemplateColumns: `160px repeat(${cols.length}, minmax(0, 1fr))`,
    }} data-testid="campaign-compare-grid">
      {/* Column headers */}
      <div/>
      {cols.map((c, i) => (
        <div key={c.campaign} className="p-3 rounded-lg text-white font-extrabold"
          style={{ background: COL_COLORS[i], color: "#0f172a", fontSize: 13 }}>
          <div className="truncate" title={c.campaign}>{c.campaign}</div>
        </div>
      ))}

      {/* KPI rows */}
      <MetricRow label="Leads totales" icon={<Zap size={14}/>}
        values={cols.map((c) => ({ value: fmt(c.leads_total), raw: c.leads_total, winner: c.winner_of?.includes("leads_total") }))}/>
      <MetricRow label="Conversiones" icon={<TrendingUp size={14}/>} accent
        values={cols.map((c) => ({ value: fmt(c.matched_leads), raw: c.matched_leads, winner: c.winner_of?.includes("matched_leads") }))}/>
      <MetricRow label="Tasa de cierre" icon={<Trophy size={14}/>} accent
        values={cols.map((c) => ({ value: `${c.conversion_rate}%`, raw: c.conversion_rate, winner: c.winner_of?.includes("conversion_rate") }))}/>
      <MetricRow label="Revenue total" icon={<Building size={14}/>}
        values={cols.map((c) => ({ value: fmtRD(c.total_revenue), raw: c.total_revenue, winner: c.winner_of?.includes("total_revenue") }))}/>
      <MetricRow label="Ticket promedio" icon={<Building size={14}/>}
        values={cols.map((c) => ({ value: fmtRD(c.avg_ticket), raw: c.avg_ticket, winner: c.winner_of?.includes("avg_ticket") }))}/>

      {/* Top branches */}
      <BreakdownRow label="Top sucursales" icon={<MapPin size={14}/>}
        cols={cols} field="top_branches" total={(c) => c.matched_leads}/>
      {/* Top brands sold */}
      <BreakdownRow label="Top marcas vendidas" icon={<Building size={14}/>}
        cols={cols} field="top_brands" total={(c) => c.matched_leads}/>
      {/* Top channels */}
      <BreakdownRow label="Canales del lead" icon={<Zap size={14}/>}
        cols={cols} field="top_channels" total={(c) => c.leads_total}/>
    </div>
  );
}

function MetricRow({ label, icon, values, accent = false }) {
  return (
    <>
      <div className="flex items-center gap-2 py-3 pr-2 border-t border-slate-100">
        <span className="text-gray-500">{icon}</span>
        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">{label}</span>
      </div>
      {values.map((v, i) => (
        <div key={i} className="flex items-center gap-2 py-3 px-3 border-t border-slate-100"
          style={{
            background: v.winner ? "#fef3c7" : (accent ? "#f8fafc" : undefined),
            borderRadius: v.winner ? 8 : 0,
          }}>
          {v.winner && <Trophy size={13} style={{ color: "#a16207" }}/>}
          <span className="font-display font-extrabold text-lg tabular-nums"
            style={{ letterSpacing: "-0.02em", color: v.winner ? "#78350f" : "#0f172a" }}>
            {v.value}
          </span>
        </div>
      ))}
    </>
  );
}

function BreakdownRow({ label, icon, cols, field, total }) {
  const maxCount = Math.max(1, ...cols.flatMap((c) => (c[field] || []).map((x) => x.count)));
  return (
    <>
      <div className="flex items-center gap-2 py-3 pr-2 border-t border-slate-100">
        <span className="text-gray-500">{icon}</span>
        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">{label}</span>
      </div>
      {cols.map((c, i) => {
        const items = c[field] || [];
        const denom = total(c) || 1;
        return (
          <div key={i} className="py-3 px-3 space-y-1.5 border-t border-slate-100">
            {items.length === 0 && (
              <span className="text-xs italic text-gray-400 font-semibold">Sin data</span>
            )}
            {items.map((it) => (
              <div key={it.name}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-bold truncate" title={it.name}>{it.name}</span>
                  <span className="font-extrabold tabular-nums text-gray-800">
                    {fmt(it.count)} <span className="text-gray-400 font-normal">
                      ({((it.count / denom) * 100).toFixed(0)}%)
                    </span>
                  </span>
                </div>
                <div className="h-1 rounded-full mt-0.5 overflow-hidden"
                  style={{ background: "#f1f5f9" }}>
                  <div className="h-full rounded-full transition-all"
                    style={{
                      width: `${(it.count / maxCount) * 100}%`,
                      background: COL_COLORS[i],
                    }}/>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}
